import React from "react";
import {
  MobilePopoverCard,
  ActionLink,
  CloseButtonIcon,
  DesktopPopupModal,
  AccordionCollection,
  SanitizedHtml,
} from "halifax";
import { Box, Typography } from "@material-ui/core";
import { VendorMessages } from "redmond";

import { IMPORTANT_INFORMATION } from "./constants";
import "./styles.scss";

export interface ICarVendorMessagesPopupProps {
  isMobile: boolean | undefined;
  onClose: () => void;
  vendorMessages?: VendorMessages | undefined;
}

const defaultProps: Partial<ICarVendorMessagesPopupProps> = {
  vendorMessages: {},
};

interface IFlatVendorMessage {
  title: string;
  text: string;
}

export const flattenVendorMessages = (
  vendorMessages?: VendorMessages
): IFlatVendorMessage[] => {
  if (!vendorMessages) return [];

  const sections = [
    vendorMessages.bookingConditions,
    vendorMessages.carRentalPolicy,
    vendorMessages.importantInfo,
    vendorMessages.pickUpDirections,
  ];

  return sections.flatMap((section) => {
    if (!section) return [];
    return section.details
      .filter((detail) => Boolean(detail.text))
      .map((detail) => ({
        title: detail.title || section.desc,
        text: detail.displayText || detail.text,
      }));
  });
};

const CarVendorMessagesPopup = ({
  isMobile,
  onClose,
  vendorMessages,
}: ICarVendorMessagesPopupProps) => {
  const messages = flattenVendorMessages(vendorMessages);

  const accordionContents = messages.map((message) => ({
    title: (
      <Typography className="vendor-message-title" variant="body1">
        {message.title}
      </Typography>
    ),
    body: (
      <SanitizedHtml
        as={Typography}
        className="content"
        variant="body2"
        html={message.text}
      />
    ),
  }));

  const renderContent = () => (
    <AccordionCollection
      className="car-vendor-messages-accordion"
      accordionContents={accordionContents}
    />
  );

  if (isMobile) {
    return (
      <MobilePopoverCard
        open
        onClose={() => onClose()}
        centered
        contentClassName="car-vendor-messages-mobile-popover-content"
        headerElement={
          <Box className="car-vendor-messages-popup-header">
            <Box className="car-vendor-messages-popup-header-top">
              <Typography className="header-title" variant="body1">
                {IMPORTANT_INFORMATION}
              </Typography>
              <ActionLink
                className="modal-close-button"
                content={<CloseButtonIcon />}
                label="Close"
                onClick={() => onClose()}
              />
            </Box>
            <Box className="header-divider" />
          </Box>
        }
        className="car-vendor-messages-popup"
      >
        <Box className="car-vendor-messages-popup-content">
          {renderContent()}
        </Box>
      </MobilePopoverCard>
    );
  }
  return (
    <DesktopPopupModal
      className="car-vendor-messages-popover-content"
      open
      onClose={() => onClose()}
      invisibleBackdrop={false}
      headerElement={
        <Box className="car-vendor-messages-popup-desktop-header">
          <Typography className="title" variant="body1">
            {IMPORTANT_INFORMATION}
          </Typography>
          <Box className="header-divider" />
        </Box>
      }
    >
      <Box className="car-vendor-messages-popup-content">
        {renderContent()}
      </Box>
    </DesktopPopupModal>
  );
};

CarVendorMessagesPopup.defaultProps = defaultProps;

export default CarVendorMessagesPopup;
